import React, { useEffect, useState } from 'react'
import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material'
import { useQuery } from '@apollo/client'
import { Button, colors } from '@resiliatech/birdsong'
import GET_TEMPLATE_LIBRARY from 'Apollo/Queries/GetTemplateLibrary'
import {
  ChecklistItemTemplate,
  GetTemplateLibraryQuery,
  TemplateLibrary,
} from 'Generated/graphql-types'
import { TemplateGoalCreationContainer } from './TemplateGoalCreation.styled'
import { Cell, Text } from './FunderSharedGoals.styled'

type TemplateGoalCreationProps = {
  onSelectTemplate: (template: TemplateLibrary) => void
}

export const TemplateGoalCreation = ({
  onSelectTemplate,
}: TemplateGoalCreationProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState<
    TemplateLibrary | undefined
  >()
  const [tasks, setTasks] = useState<ChecklistItemTemplate[]>([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(100)

  const { data: { getTemplateLibrary = [] } = {}, loading } =
    useQuery<GetTemplateLibraryQuery>(GET_TEMPLATE_LIBRARY)

  useEffect(() => {
    if (selectedTemplate) {
      setTasks(selectedTemplate.checklistItemTemplate)
    }
  }, [selectedTemplate])

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  const handleSelectTemplate = (template: TemplateLibrary) => {
    setSelectedTemplate(template)
    onSelectTemplate(template)
  }

  if (loading) return <CircularProgress />

  const filteredTemplates = getTemplateLibrary.filter((t) => t.showInListing)
  const templates = filteredTemplates
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    .map((template) => (
      <TableRow
        hover={template.id !== selectedTemplate?.id}
        key={template.id}
        sx={{
          backgroundColor:
            template.id === selectedTemplate?.id
              ? colors.interactive.shadowFocusOrange
              : undefined,
        }}
      >
        <TableCell>
          {template.goalTemplate[0]?.title || '<NO GOAL FOUND>'}
        </TableCell>
        <TableCell>
          {template.goalTemplate[0]?.category || '<NO GOAL FOUND>'}
        </TableCell>
        <TableCell>{template.id}</TableCell>
        <TableCell>{template.appcuesFlowId}</TableCell>
        <TableCell>{template.description}</TableCell>
        <TableCell>
          <Button
            label='Select'
            onClick={() => handleSelectTemplate(template as TemplateLibrary)}
            type='button'
            variant='secondary'
          />
        </TableCell>
      </TableRow>
    ))

  return (
    <TemplateGoalCreationContainer>
      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 300 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <Cell>Goal Title</Cell>
                <Cell>Goal Category</Cell>
                <Cell>Template ID</Cell>
                <Cell>Appcues Flow ID</Cell>
                <Cell>Description</Cell>
                <Cell>Select</Cell>
              </TableRow>
            </TableHead>
            <TableBody>{templates}</TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component='div'
          count={filteredTemplates.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      {!!tasks.length && (
        <>
          <Text>Tasks to be created for the selected template:</Text>
          <Paper sx={{ overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Task Title</TableCell>
                    <TableCell>Academy Resource ID</TableCell>
                    <TableCell>Link Text</TableCell>
                    <TableCell>Link URL</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks.map((task) => (
                    <TableRow key={task.id}>
                      <TableCell>{task.title}</TableCell>
                      <TableCell>{task.resourceId}</TableCell>
                      <TableCell>{task.linkText}</TableCell>
                      <TableCell>{task.linkUrl}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </TemplateGoalCreationContainer>
  )
}
